/**
 * Sync (PLAN-Z M9): on login/register push all unsynced local attempts, pull
 * the account history and union-merge into IndexedDB; steady state is a
 * fire-and-forget upsert after each answer. Failed or offline pushes simply
 * leave the attempt unsynced — the unsynced set IS the retry queue, drained
 * by the next syncNow() (login, app start, browser 'online' event).
 */
import { createStore, get, set } from 'idb-keyval'
import { loadAttempts, saveAttempt, type Attempt } from './local.ts'
import { fromRow, getSupabase, toRow } from './supabase.ts'
import { mergeAttempts } from './merge.ts'

// Own database: IndexedDB never adds stores to an existing database, and the
// 'dmat-prep' database predates sync — a fresh DB avoids the upgrade problem.
const syncStore = createStore('dmat-prep-sync', 'sync')
const SYNCED_KEY = 'synced-attempt-ids'

async function getSynced(): Promise<Set<string>> {
  return new Set<string>((await get<string[]>(SYNCED_KEY, syncStore)) ?? [])
}

async function markSynced(ids: string[]): Promise<void> {
  const set_ = await getSynced()
  for (const id of ids) set_.add(id)
  await set(SYNCED_KEY, [...set_], syncStore)
}

export interface SyncResult {
  ok: boolean
  pushed: number
  pulled: number
  error?: string
}

/** Full sync: push unsynced → pull remote → union-merge by attempt id. */
export async function syncNow(): Promise<SyncResult | null> {
  const sb = getSupabase()
  if (!sb) return null
  const { data: userData } = await sb.auth.getUser()
  const user = userData?.user
  if (!user) return null
  try {
    const local = await loadAttempts()
    const synced = await getSynced()
    const pending = local.filter((a) => !synced.has(a.id))

    let pushed = 0
    if (pending.length > 0) {
      const { error } = await sb
        .from('attempts')
        .upsert(pending.map((a) => toRow(a, user.id)), { onConflict: 'id' })
      if (error) return { ok: false, pushed: 0, pulled: 0, error: error.message }
      pushed = pending.length
    }

    const { data, error: pullError } = await sb
      .from('attempts')
      .select('*')
      .eq('user_id', user.id)
    if (pullError) return { ok: false, pushed, pulled: 0, error: pullError.message }
    const remote = (data ?? []).map(fromRow)

    const merged = mergeAttempts(local, remote)
    const remoteOnly = merged.filter((m) => !local.some((l) => l.id === m.id))
    for (const a of remoteOnly) await saveAttempt(a)
    await markSynced(merged.map((a) => a.id))

    return { ok: true, pushed, pulled: remoteOnly.length }
  } catch (error) {
    return { ok: false, pushed: 0, pulled: 0, error: String(error) }
  }
}

/** Fire-and-forget push after each answer. No client, no user, or offline →
 * no-op; the attempt stays unsynced and rides the next syncNow(). */
export async function pushAttempts(attempts: Attempt[]): Promise<void> {
  const sb = getSupabase()
  if (!sb) return
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return
  const { data: userData } = await sb.auth.getUser()
  const user = userData?.user
  if (!user) return
  const synced = await getSynced()
  const pending = attempts.filter((a) => !synced.has(a.id))
  if (pending.length === 0) return
  const { error } = await sb
    .from('attempts')
    .upsert(pending.map((a) => toRow(a, user.id)), { onConflict: 'id' })
  if (!error) await markSynced(pending.map((a) => a.id))
}

/** Install the retry triggers: browser back online, and after login. Call
 * once from the app root. */
export function installSyncListeners(): void {
  if (typeof window === 'undefined') return
  window.addEventListener('online', () => {
    void syncNow()
  })
}
